(ns app.front.view.application.target-times
  (:require [app.domain.blackout.model :as blackout-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.target-time.model :as target-time-model]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.util :as futil]
            [app.front.view.workspace.section :as ws-section]
            [app.front.view.workspace.subs :as ws-subs]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(rf/reg-sub ::general-blackouts
  :<- [::ws-subs/authority-group]
  :<- [::ws-subs/application]
  (fn [[authority-group application] _]
    (blackout-model/current-group-general-blackouts authority-group
                                                    (date-util/start-of-day (:application/submitted application)))))


(rf/reg-sub ::manual-inputs?
  :<- [::ws-subs/workspace]
  (fn [ws _]
    (config-model/resolve-boolean :group.config/target-time-manual-inputs? ws)))


(rf/reg-sub ::target-time-values
  :<- [::ws-subs/application]
  :<- [::ws-subs/last-state-change-date]
  :<- [::ws-subs/group-time-zone]
  :<- [::general-blackouts]
  (fn [[application last-state-change-date timezone blackouts] _]
    (-> (util/mmap application last-state-change-date timezone blackouts)
        target-time-model/target-time-details
        (p/update-if-contains :due-date date-util/start-of-day))))


(rf/reg-sub ::days-left
  :<- [::target-time-values]
  :<- [::general-blackouts]
  (fn [[{:keys [start-date due-date unit]} blackouts] _]
    (when (and unit due-date (<= (date-util/start-of-day start-date) (date-util/today-start-of-day)))
      (target-time-model/count-due-days {:start-date (date-util/today-start-of-day)
                                         :end-date   due-date
                                         :unit       unit
                                         :blackouts  blackouts}))))


(rf/reg-sub ::days-used
  :<- [::target-time-values]
  :<- [::days-left]
  (fn [[{:keys [value]} days-left] _]
    (- value (max 0 days-left))))


(rf/reg-sub ::business-days?
  :<- [::target-time-values]
  (fn [{:keys [unit]} _]
    (= unit :target-time.unit/business-days)))


(rf/reg-sub ::paused-date
  :<- [::ws-subs/application]
  (fn [application _]
    (-> application :application/target-time-paused)))


(rf/reg-sub ::paused?
  :<- [::paused-date]
  :-> some?)


(rf/reg-sub ::paused-days
  :<- [::paused-date]
  :<- [::target-time-values]
  :<- [::general-blackouts]
  (fn [[paused-date {:keys [unit]} blackouts] _]
    (if (inst? paused-date)
      (-> (target-time-model/count-due-days {:start-date paused-date
                                             :end-date   (js/Date.)
                                             :unit       unit
                                             :blackouts  blackouts})
          (max 0))
      0)))


(rf/reg-sub ::editing?
  (fn [db _]
    (-> db ::editing?)))


(rf/reg-sub ::start-date-picker-value
  (fn [db _]
    (-> db ::start-date-picker-value)))


(rf/reg-sub ::end-date-picker-value
  (fn [db _]
    (-> db ::end-date-picker-value)))


(rf/reg-sub ::start-date-picker-value-with-default
  :<- [::start-date-picker-value]
  :<- [::target-time-values]
  (fn [[start-picker-value {:keys [start-date]}] _]
    (or start-picker-value
        start-date)))


(rf/reg-sub ::end-date-picker-value-with-default
  :<- [::end-date-picker-value]
  :<- [::target-time-values]
  (fn [[end-picker-value {:keys [end-date due-date]}] _]
    (or end-picker-value
        due-date
        end-date)))


(rf/reg-sub ::date-picker-initial-value
  :<- [::target-time-values]
  :<- [::paused-days]
  :<- [::general-blackouts]
  (fn [[{:keys [due-date unit business-day-end-time calculation-starting-day timezone]} paused-days blackouts] _]
    (target-time-model/calculate-due-date (util/mmap {:start-date (or due-date (date-util/today-start-of-day))
                                                      :days       paused-days}
                                                     unit blackouts business-day-end-time calculation-starting-day timezone))))


(rf/reg-sub ::date-picker-values-to-due-days
  :<- [::target-time-values]
  :<- [::start-date-picker-value-with-default]
  :<- [::end-date-picker-value-with-default]
  :<- [::general-blackouts]
  (fn [[{:keys [start-date due-date unit calculation-starting-day]} start-picker-value end-picker-value blackouts] [_ end-date-picker?]]
    (let [start (if end-date-picker?
                  start-date
                  start-picker-value)
          end   (if end-date-picker?
                  end-picker-value
                  due-date)]
      (when (and (some? start) (some? end))
        (let [days (-> (target-time-model/count-due-days {:start-date start
                                                          :end-date   end
                                                          :unit       unit
                                                          :blackouts  blackouts})
                       (max 0))]
          (if (= calculation-starting-day :target-time.starting-day/current-day)
            (inc days)
            days))))))


(rf/reg-sub ::until-due-text
  :<- [::paused?]
  :<- [::days-left]
  :<- [::business-days?]
  (fn [[paused? days-left business-days?] _]
    (target-time-model/until-due-text (util/mmap paused? days-left business-days?))))


(rf/reg-event-db ::toggle-editing
  (fn [db _]
    (-> db
        (update ::editing? not)
        (dissoc ::end-date-picker-value))))

(rf/reg-event-db ::close-editing
  (fn [db]
    (dissoc db ::editing?)))


(rf/reg-event-db ::resume
  (fn [db [_ date-picker-value]]
    (-> db
        (assoc ::editing? true
               ::end-date-picker-value date-picker-value))))


(rf/reg-event-db ::set-date-picker-value
  (fn [db [_ value end-date-picker?]]
    (if end-date-picker?
      (assoc db ::end-date-picker-value value)
      (assoc db ::start-date-picker-value value))))


(rf/reg-event-db ::clear-date-picker-values
  ;; Once the dates are stored, the application is the source of truth again. Without this the pickers
  ;; would keep showing the last typed values even when the target times are changed elsewhere, e.g.
  ;; when they are reset while taking the application back to Submitted.
  (fn [db _]
    (dissoc db ::start-date-picker-value ::end-date-picker-value)))


(rf/reg-event-fx ::save-target-date
  (fn [_ [_ application-id manual-inputs? value-field value ts-field ts]]
    {:dispatch-n [[::process/signal
                   application-id
                   [:process.signal/set-target-times (merge (when value
                                                              {value-field value})
                                                            (when ts
                                                              {ts-field ts}))]
                   {:notify     {:success :changes-saved}
                    :on-success [::clear-date-picker-values]}]
                  (when (not manual-inputs?)
                    [::toggle-editing])]}))


(rf/reg-event-fx ::set-pause
  (fn [_ [_ application-id]]
    {:dispatch [::process/signal
                application-id
                [:process.signal/set-target-time-pause]
                {:notify {:success :target-time/calculation-paused}}]}))


(defn actions-dropdown [tr application]
  (let [editing?                  (<sub [::editing?])
        paused?                   (<sub [::paused?])
        edit-permitted?           (and (<sub [::process/app-signal-permitted? [:process.signal/set-target-times]])
                                       (not (<sub [::manual-inputs?])))
        pause-permitted?          (<sub [::process/app-signal-permitted? [:process.signal/set-target-time-pause]])
        date-picker-initial-value (<sub [::date-picker-initial-value])
        actions                   (->> (if paused?
                                         [(when edit-permitted? :target-time/resume)]
                                         [(when edit-permitted? :target-time/edit)
                                          (when pause-permitted? :target-time/pause)])
                                       (remove nil?))
        label                     (tr [:target-time/actions])]
    (when (and (not editing?) (seq actions))
      (cc/dropdown
        {:id            "target-times-actions-select"
         :label         ""
         :hide-label    true
         :title-text    label
         :class-name    "select-action w-36"
         :items         (for [action actions]
                          {:label (tr [action])
                           :value ((juxt namespace name) action)})
         :selected-item {:label label}
         :on-change     #(case (apply keyword (futil/oget % :selectedItem :value))
                           :target-time/edit (>evt [::toggle-editing])
                           :target-time/resume (>evt [::resume date-picker-initial-value])
                           :target-time/pause (>evt [::set-pause (:db/id application)]))}))))


(defn target-date-picker [tr start-date due-date value-attr ts-attr end-date-picker?]
  (let [application       (<sub [::ws-subs/application])
        days-value        (<sub [::date-picker-values-to-due-days end-date-picker?])
        paused?           (<sub [::paused?])
        manual-inputs?    (<sub [::manual-inputs?])
        date-picker-value (if end-date-picker?
                            (<sub [::end-date-picker-value-with-default])
                            (<sub [::start-date-picker-value-with-default]))]
    [:div.flex.flex-col.md:flex-row.items-stretch.mt-4.md:mt-0
     [:div.text-xs.flex.items-center.flex-row.mr-2 (if end-date-picker?
                                                     (tr [:target-time/due-date])
                                                     (tr [:target-time/start-date]))]
     [ui/date-picker {:id         (if end-date-picker?
                                    "target-time-due-date-picker"
                                    "target-time-start-date-picker")
                      :class-name "animation-appear-slide-down"
                      :callback   #(>evt [::set-date-picker-value (date-util/start-of-day %) end-date-picker?])
                      :value      date-picker-value
                      :invalid?   (nil? days-value)
                      :min-date   (when end-date-picker?
                                    start-date)
                      :max-date   (when (not end-date-picker?)
                                    due-date)}]
     [:div.flex.flex-row.items-stretch.mt-2.md:mt-0
      (when (not manual-inputs?)
        (cc/button {:kind       :tertiary
                    :size       :small
                    :on-click   #(>evt [::toggle-editing])
                    :class-name "md:ml-2"}
                   (tr [:cancel])))
      (cc/button {:kind       :primary
                  :size       :small
                  :disabled   (true? (when (not paused?)
                                       (if end-date-picker?
                                         (= date-picker-value due-date)
                                         (= date-picker-value start-date))))
                  :on-click   #(>evt [::save-target-date (:db/id application) manual-inputs? value-attr days-value
                                      ts-attr date-picker-value])
                  :class-name "ml-2"}
                 (tr [(if paused? :target-time/resume :save)]))]]))


(defn target-times-section-content [tr]
  (let [days-left          (<sub [::days-left])
        days-used          (<sub [::days-used])
        until-due-tr       (<sub [::until-due-text])
        editing?           (<sub [::editing?])
        paused?            (<sub [::paused?])
        manual-inputs?     (<sub [::manual-inputs?])
        set-permitted?     (<sub [::process/app-signal-permitted? [:process.signal/set-target-times]])
        {:keys [start-date
                due-date
                value
                value-attr
                state]}    (<sub [::target-time-values])
        fields             (target-time-model/state->fields state)]
    [:div
     [:div.flex.flex-row.justify-between.flex-wrap.leading-snug
      [:div.md:order-1.md:flex-1
       (if (and set-permitted? manual-inputs?)
         [target-date-picker tr start-date due-date value-attr (:manual-start fields) false]
         [:<>
          [:div (tr [:target-time/start-date])]
          [:div ((<sub [::date-util/date-time-fn]) start-date)]])]
      [:div.text-right.md:order-3.md:flex-1.flex.flex-col.items-end
       (if (and set-permitted?
                (or editing? manual-inputs?))
         [target-date-picker tr start-date due-date value-attr (:manual-end fields) true]
         (when-not paused?
           [:<>
            [:div (tr [:target-time/due-date])]
            [:div ((<sub [::date-util/date-fn]) due-date)]]))]
      (when-not editing?
        [:div.text-center.bg-ui-blue-10.py-2.px-4.rounded-md.md:order-2.mt-2.md:-mt-1.md:max-w-md.w-full.md:w-auto.flex.items-center
         (tr [state])])]
     [:div.py-4
      [:div.bg-gray-200.relative.rounded-md
       [:div.h-full.absolute.top-0.left-0.rounded-md
        {:class (cond
                  paused?              "bg-gray-300"
                  (neg-int? days-left) "bg-ui-red-20"
                  :else                "bg-ui-blue-30")
         :style {:width (str (/ days-used value 0.01) "%") :transition "all 200ms ease-in-out"}}]
       (when (some? days-left) [:div.text-center.font-bold.text-lg.py-3.relative (tr [until-due-tr] [(abs days-left)])])]]]))

(rf/reg-sub ::show-section?
  :<- [::target-time-values]
  :<- [::days-left]
  :<- [::paused?]
  :<- [::process/app-signal-permitted? [:process.signal/set-target-times]]
  :<- [::ws-subs/workspace]
  :<- [::ws-subs/user-has-permission? :permission.phase/authority-read]
  :<- [::manual-inputs?]
  (fn [[{:keys [value state]} days-left paused? set-allowed? ws authority? manual-inputs?] _]
    (and (or (not (config-model/resolve-boolean :group.config/target-times-visible-for-authority-only? ws))
             authority?)
         (case state
           ;; if ::target-time-values gives us nil, there isn't much to show
           nil false
           ;; in appeal state, only count to zero, then hide
           :target-time/waiting-for-appeal (and (or (not paused?)
                                                    set-allowed?)
                                                (or (and (nil? days-left) manual-inputs?)
                                                    (> days-left 0)))
           (or paused?
               (and manual-inputs?
                    set-allowed?)
               (some? value))))))

(defn target-times-section []
  (let [tr          (<sub [::ws-subs/tr])
        application (<sub [::ws-subs/application])]
    (when (<sub [::show-section?])
      [ws-section/section
       application
       :target-times-section
       {:heading-button [actions-dropdown tr application]
        :tr             tr}
       :target-time/section-title
       true
       [target-times-section-content tr]])))
